import { useEffect, useState } from 'react';

/**
 * Mini router via hash routing. Sem dependência externa.
 *
 * Uso:
 *   const { path, query, navigate } = useRoute();
 *   if (path === '/login') return <LoginRoute />;
 *   navigate('/login') // ou navigate('/reset-password', { token: '...' })
 *
 * Hash routing escolhido porque:
 *   - Não precisa de servidor reescrever URLs (works com Vite dev e nginx static).
 *   - Reload da página mantém rota.
 *   - Browser back/forward funcionam naturalmente.
 */

export interface RouteState {
  path: string;
  query: Record<string, string>;
}

function parseHash(hash: string): RouteState {
  const stripped = hash.startsWith('#') ? hash.slice(1) : hash;
  const trimmed = stripped.startsWith('/') ? stripped : `/${stripped}`;
  const [path, queryStr = ''] = trimmed.split('?');
  const query: Record<string, string> = {};
  if (queryStr) {
    const params = new URLSearchParams(queryStr);
    params.forEach((value, key) => {
      query[key] = value;
    });
  }
  return { path: path || '/', query };
}

export function useRoute(): RouteState & { navigate: (to: string, query?: Record<string, string>) => void } {
  const [state, setState] = useState<RouteState>(() => parseHash(window.location.hash));

  useEffect(() => {
    function onHashChange() {
      setState(parseHash(window.location.hash));
    }
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  function navigate(to: string, query?: Record<string, string>): void {
    let target = to.startsWith('/') ? to : `/${to}`;
    if (query && Object.keys(query).length > 0) {
      const params = new URLSearchParams(query);
      target += `?${params.toString()}`;
    }
    if (window.location.hash !== `#${target}`) {
      window.location.hash = target;
    }
  }

  return { ...state, navigate };
}

/**
 * Hook utilitário para redirecionar quando uma condição é satisfeita.
 *
 *   useRedirect(user === null, '/login')
 */
export function useRedirect(condition: boolean, to: string, query?: Record<string, string>): void {
  const { navigate, path } = useRoute();
  useEffect(() => {
    if (condition && path !== to) {
      navigate(to, query);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condition, to, path]);
}
